import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Rocket, GitBranch, Search, Check, Loader2, AlertTriangle,
  ChevronDown, Server, Radio, ShieldCheck, Zap,
} from 'lucide-react';
import {
  getTenants,
  getSimulcastTenants,
  getDeployBranches,
  startFullDeploy,
  getDeploySession,
  getDeployHistory,
  getPendingApprovals,
  approveDeployment,
  rejectDeployment,
} from '../../services/deploymentsApi';

// ─── Constants ──────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 5000;
const CONFIRM_PHRASE_STAGING = 'DEPLOY TO STAGING';
const CONFIRM_PHRASE_DIRECT = 'DEPLOY TO PRODUCTION';
const LS_KEY = 'deploybeta.selection.v1';

const PRODUCTS = {
  backoffice: { key: 'backoffice', label: 'BackOffice', short: 'BO', icon: Server, accent: 'blue', pipelineCount: 7 },
  simulcast: { key: 'simulcast', label: 'Simulcast', short: 'SIM', icon: Radio, accent: 'violet', pipelineCount: 3 },
};

const TERMINAL = ['completed', 'partiallyFailed', 'failed'];
const isTerminal = (s) => TERMINAL.includes(s);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const uniq = (arr) => [...new Set(arr)];

// ─── Small presentational pieces ──────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending: ['bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300', 'Pending'],
    inProgress: ['bg-blue-100 text-blue-700 animate-pulse', 'Running…'],
    succeeded: ['bg-green-100 text-green-700', 'Succeeded'],
    failed: ['bg-red-100 text-red-700', 'Failed'],
  };
  const [cls, label] = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status === 'inProgress' && (
        <span className="inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      )}
      {label}
    </span>
  );
};

const overallStatusMeta = (status) => {
  switch (status) {
    case 'completed': return ['bg-green-100 dark:bg-green-900 text-green-700', 'Completed'];
    case 'partiallyFailed': return ['bg-yellow-100 dark:bg-yellow-900 text-yellow-700', 'Partial'];
    case 'failed': return ['bg-red-100 dark:bg-red-900 text-red-700', 'Failed'];
    case 'releasing': return ['bg-blue-100 dark:bg-blue-900 text-blue-700 animate-pulse', 'Releasing…'];
    case 'building': return ['bg-blue-100 dark:bg-blue-900 text-blue-700 animate-pulse', 'Building…'];
    default: return ['bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400', cap(status || 'pending')];
  }
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return d; }
};

// ─── Searchable branch picker ──────────────────────────────────────────────────
const BranchPicker = ({ branches, value, loading, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setSearch(''); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = q ? branches.filter((b) => b.name.toLowerCase().includes(q)) : branches;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        disabled={loading}
        onClick={() => { setOpen((o) => !o); setSearch(''); }}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 disabled:opacity-60 hover:border-gray-400 dark:hover:border-slate-600"
      >
        <span className="flex items-center gap-2 min-w-0">
          <GitBranch className="w-4 h-4 text-gray-400 shrink-0" />
          <span className={`truncate text-sm ${value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}>
            {loading ? 'Loading branches…' : value || 'Select branch'}
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100 dark:border-slate-800">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-700 rounded-md">
              <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <input
                ref={searchRef}
                type="text"
                className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400 dark:text-gray-100"
                placeholder="Search branches…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setSearch(''); } }}
              />
            </div>
          </div>
          <ul className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-gray-400 text-center">No branches match "{search}"</li>
            )}
            {filtered.map((b) => (
              <li
                key={b.name}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm select-none ${
                  b.name === value
                    ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 font-medium'
                    : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800'
                }`}
                onMouseDown={(e) => { e.preventDefault(); onChange(b.name); setOpen(false); setSearch(''); }}
              >
                <GitBranch className="w-3.5 h-3.5 shrink-0 opacity-60" />
                <span className="truncate flex-1">{b.name}</span>
                {b.isDefault && <span className="text-xs text-gray-400 shrink-0">default</span>}
                {b.name === value && <Check className="w-4 h-4 shrink-0 text-blue-600" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Pipeline status table (used in progress + history) ─────────────────────────
const PipelineTable = ({ pipelines, approvals = [] }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 dark:border-slate-700">
          <th className="text-left py-1.5 font-medium text-gray-600 dark:text-gray-400">Pipeline</th>
          <th className="text-left py-1.5 font-medium text-gray-600 dark:text-gray-400">Build</th>
          <th className="text-left py-1.5 font-medium text-gray-600 dark:text-gray-400">Release</th>
        </tr>
      </thead>
      <tbody>
        {(pipelines || []).map((p, i) => {
          const awaiting = p.releaseStatus === 'inProgress' && approvals.some(
            (a) => a.pipelineId === p.releaseRunId || (a.pipelineName && p.slug && a.pipelineName.toLowerCase().includes(p.slug))
          );
          return (
            <tr key={p.slug || i} className="border-b border-gray-100 dark:border-slate-800">
              <td className="py-1.5 font-medium">{p.name}</td>
              <td className="py-1.5">
                <span className="flex items-center gap-2">
                  <StatusBadge status={p.buildStatus} />
                  {p.buildUrl && <a href={p.buildUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View</a>}
                </span>
                {p.buildStatus === 'failed' && p.errorMessage && (
                  <p className="text-xs text-red-500 mt-0.5 truncate max-w-[220px]" title={p.errorMessage}>{p.errorMessage}</p>
                )}
              </td>
              <td className="py-1.5">
                <span className="flex items-center gap-2">
                  {awaiting ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                      <AlertTriangle className="w-3 h-3" /> Awaiting Approval
                    </span>
                  ) : (
                    <StatusBadge status={p.releaseStatus} />
                  )}
                  {p.releaseUrl && <a href={p.releaseUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View</a>}
                </span>
                {p.releaseStatus === 'failed' && p.errorMessage && (
                  <p className="text-xs text-red-500 mt-0.5 truncate max-w-[220px]" title={p.errorMessage}>{p.errorMessage}</p>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ─── Main component ─────────────────────────────────────────────────────────────
const TenantDeploy = () => {
  const [boTenants, setBoTenants] = useState({ nonProd: [], prod: [] });
  const [simTenants, setSimTenants] = useState({ nonProd: [], prod: [] });
  const [branches, setBranches] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [branchesLoading, setBranchesLoading] = useState(true);

  // Restore last selection
  const saved = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
  }, []);
  const [selected, setSelected] = useState(() => saved.tenants || []);
  const [branch, setBranch] = useState(() => saved.branch || 'development');
  const [products, setProducts] = useState(() => saved.products || ['backoffice', 'simulcast']);
  const [mode, setMode] = useState('staging'); // staging | direct

  const [phase, setPhase] = useState('select'); // select | progress
  const [jobs, setJobs] = useState([]); // [{ product, label, tenants, id }]
  const [sessions, setSessions] = useState({}); // id -> DeploymentSession
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  const [approvals, setApprovals] = useState([]);
  const [approvalBusy, setApprovalBusy] = useState(null);
  const [approvalComments, setApprovalComments] = useState({});
  const [approvalMsg, setApprovalMsg] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(null);

  // ── Loaders ──
  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    Promise.all([getTenants(), getSimulcastTenants()])
      .then(([bo, sc]) => {
        if (cancelled) return;
        setBoTenants({ nonProd: bo.nonProd || [], prod: bo.prod || [] });
        setSimTenants({ nonProd: sc.nonProd || [], prod: sc.prod || [] });
      })
      .catch(() => {})
      .finally(() => !cancelled && setTenantsLoading(false));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setBranchesLoading(true);
    getDeployBranches()
      .then((data) => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : [];
        setBranches(arr);
        const names = arr.map((b) => b.name);
        setBranch((prev) => (names.includes(prev) ? prev : names.includes('development') ? 'development' : names[0] || ''));
      })
      .catch(() => !cancelled && setBranches([]))
      .finally(() => !cancelled && setBranchesLoading(false));
    return () => { cancelled = true; };
  }, []);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try { const d = await getDeployHistory(50); setHistory(Array.isArray(d) ? d : []); }
    catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  }, []);

  const refreshApprovals = useCallback(async () => {
    try { const l = await getPendingApprovals(); setApprovals(Array.isArray(l) ? l : []); }
    catch { setApprovals([]); }
  }, []);

  useEffect(() => { refreshHistory(); refreshApprovals(); }, [refreshHistory, refreshApprovals]);
  useEffect(() => {
    const t = setInterval(refreshApprovals, 10000);
    return () => clearInterval(t);
  }, [refreshApprovals]);

  // Persist selection
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify({ tenants: selected, branch, products })); } catch { /* ignore */ }
  }, [selected, branch, products]);

  // ── Derived tenant model ──
  const boAll = useMemo(() => uniq([...(boTenants.nonProd || []), ...(boTenants.prod || [])]), [boTenants]);
  const simAll = useMemo(() => uniq([...(simTenants.nonProd || []), ...(simTenants.prod || [])]), [simTenants]);
  const prodSet = useMemo(() => new Set([...(boTenants.prod || []), ...(simTenants.prod || [])]), [boTenants, simTenants]);

  const rows = useMemo(() => {
    const prod = uniq([...(boTenants.prod || []), ...(simTenants.prod || [])]).sort();
    const nonProd = uniq([...(boTenants.nonProd || []), ...(simTenants.nonProd || [])]).sort();
    const toRow = (t) => ({ name: t, hasBO: boAll.includes(t), hasSIM: simAll.includes(t), isProd: prodSet.has(t) });
    return { prod: prod.map(toRow), nonProd: nonProd.map(toRow) };
  }, [boTenants, simTenants, boAll, simAll, prodSet]);

  const boTargets = useMemo(
    () => (products.includes('backoffice') ? selected.filter((t) => boAll.includes(t)) : []),
    [products, selected, boAll]
  );
  const simTargets = useMemo(
    () => (products.includes('simulcast') ? selected.filter((t) => simAll.includes(t)) : []),
    [products, selected, simAll]
  );
  const skipped = useMemo(
    () => selected.filter((t) => !boTargets.includes(t) && !simTargets.includes(t)),
    [selected, boTargets, simTargets]
  );
  const anyProdTarget = useMemo(
    () => [...boTargets, ...simTargets].some((t) => prodSet.has(t)),
    [boTargets, simTargets, prodSet]
  );
  const totalPipelines = boTargets.length * PRODUCTS.backoffice.pipelineCount + simTargets.length * PRODUCTS.simulcast.pipelineCount;
  const canDeploy = !triggering && branch && products.length > 0 && (boTargets.length + simTargets.length) > 0;

  // ── Selection helpers ──
  const toggleTenant = (t) => setSelected((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const toggleProduct = (key) => setProducts((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));
  const selectGroup = (list) => setSelected((p) => uniq([...p, ...list.map((r) => r.name)]));
  const clearGroup = (list) => setSelected((p) => p.filter((t) => !list.some((r) => r.name === t)));

  // ── Deploy ──
  const confirmationPhrase = mode === 'direct' ? CONFIRM_PHRASE_DIRECT : CONFIRM_PHRASE_STAGING;

  const handleDeployClick = () => {
    if (anyProdTarget) { setShowConfirm(true); setConfirmInput(''); }
    else executeDeploy();
  };

  const executeDeploy = useCallback(async () => {
    setShowConfirm(false);
    setConfirmInput('');
    setError(null);
    const plan = [];
    if (boTargets.length) plan.push({ product: 'backoffice', label: PRODUCTS.backoffice.label, tenants: boTargets });
    if (simTargets.length) plan.push({ product: 'simulcast', label: PRODUCTS.simulcast.label, tenants: simTargets });
    if (plan.length === 0) return;

    setTriggering(true);
    setPhase('progress');
    setSessions({});
    setJobs([]);
    try {
      const started = await Promise.all(
        plan.map(async (j) => {
          const { sessionId } = await startFullDeploy(j.product, j.tenants, branch, mode);
          return { ...j, id: sessionId };
        })
      );
      setJobs(started);
      const data = {};
      await Promise.all(started.map(async (s) => { try { data[s.id] = await getDeploySession(s.id); } catch { /* ignore */ } }));
      setSessions(data);
      refreshHistory();
    } catch (err) {
      setError(err.message || 'Failed to start deployment');
      setPhase('select');
      setTriggering(false);
    }
  }, [boTargets, simTargets, branch, mode, refreshHistory]);

  // Poll active sessions
  useEffect(() => {
    if (phase !== 'progress' || jobs.length === 0) return undefined;
    const poll = async () => {
      const updates = await Promise.all(
        jobs.map(async (j) => {
          try { return [j.id, await getDeploySession(j.id)]; } catch { return [j.id, null]; }
        })
      );
      setSessions((prev) => {
        const next = { ...prev };
        updates.forEach(([id, s]) => { if (s) next[id] = s; });
        return next;
      });
      const all = updates.map(([, s]) => s).filter(Boolean);
      if (all.some((s) => s.overallStatus === 'releasing')) refreshApprovals();
      if (all.length === jobs.length && all.every((s) => isTerminal(s.overallStatus))) {
        setTriggering(false);
        refreshApprovals();
        refreshHistory();
      }
    };
    const t = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [phase, jobs, refreshApprovals, refreshHistory]);

  const allTerminal = jobs.length > 0 && jobs.every((j) => sessions[j.id] && isTerminal(sessions[j.id].overallStatus));

  const resetToSelect = () => { setPhase('select'); setJobs([]); setSessions({}); setTriggering(false); };

  // ── Approvals ──
  const doApprove = async (id, comment) => {
    setApprovalBusy(id); setApprovalMsg(null);
    try { await approveDeployment(id, comment); setApprovalMsg({ type: 'success', text: 'Approved — deployment will proceed.' }); setApprovalComments((p) => ({ ...p, [id]: '' })); }
    catch (e) { setApprovalMsg({ type: 'error', text: e.message || 'Failed to approve' }); }
    finally { setApprovalBusy(null); await refreshApprovals(); }
  };
  const doReject = async (id, comment) => {
    setApprovalBusy(id); setApprovalMsg(null);
    try { await rejectDeployment(id, comment); setApprovalMsg({ type: 'success', text: 'Rejected.' }); setApprovalComments((p) => ({ ...p, [id]: '' })); }
    catch (e) { setApprovalMsg({ type: 'error', text: e.message || 'Failed to reject' }); }
    finally { setApprovalBusy(null); await refreshApprovals(); }
  };

  // ── Render: tenant row ──
  const renderTenantRow = (r) => {
    const checked = selected.includes(r.name);
    return (
      <button
        key={r.name}
        type="button"
        onClick={() => toggleTenant(r.name)}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-all ${
          checked
            ? r.isProd
              ? 'border-red-300 bg-red-50 dark:bg-red-950/40'
              : 'border-blue-300 bg-blue-50 dark:bg-blue-950/40'
            : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-gray-300 dark:hover:border-slate-600'
        }`}
      >
        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
          checked ? (r.isProd ? 'bg-red-600 border-red-600' : 'bg-blue-600 border-blue-600') : 'border-gray-300 dark:border-slate-600'
        }`}>
          {checked && <Check className="w-3 h-3 text-white" />}
        </span>
        <span className="text-sm font-medium truncate flex-1">{cap(r.name)}</span>
        <span className="flex gap-1 shrink-0">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
            r.hasBO ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 dark:bg-slate-800 text-gray-300 dark:text-slate-600 line-through'
          }`} title={r.hasBO ? 'Has BackOffice' : 'No BackOffice'}>BO</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
            r.hasSIM ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 dark:bg-slate-800 text-gray-300 dark:text-slate-600 line-through'
          }`} title={r.hasSIM ? 'Has Simulcast' : 'No Simulcast'}>SIM</span>
        </span>
      </button>
    );
  };

  const tenantSection = (title, list, accent) => {
    if (!list.length) return null;
    const allSel = list.every((r) => selected.includes(r.name));
    return (
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className={`text-xs font-semibold uppercase tracking-wide ${accent === 'red' ? 'text-red-500' : 'text-gray-500 dark:text-slate-400'}`}>{title}</span>
          <button type="button" className={`text-xs hover:underline ${accent === 'red' ? 'text-red-600' : 'text-blue-600'}`}
            onClick={() => (allSel ? clearGroup(list) : selectGroup(list))}>
            {allSel ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">{list.map(renderTenantRow)}</div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Beta banner + link back to classic */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800">
        <Zap className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800 dark:text-amber-200">
          <strong>Beta — Per-Tenant Deploy.</strong> Pick tenants, a branch, and which products to ship. BackOffice and
          Simulcast fire together in one click. The classic{' '}
          <a href="/deployments" className="underline font-medium">Deployments</a> page is unchanged if you need it.
        </div>
      </div>

      {phase === 'select' && (
        <>
          {/* Controls: branch + products + mode */}
          <div className="grid gap-4 lg:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Branch</label>
              <BranchPicker branches={branches} value={branch} loading={branchesLoading} onChange={setBranch} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Products</label>
              <div className="flex gap-2">
                {Object.values(PRODUCTS).map((p) => {
                  const on = products.includes(p.key);
                  const Icon = p.icon;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => toggleProduct(p.key)}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        on
                          ? p.accent === 'violet'
                            ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/40 text-violet-700'
                            : 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-700'
                          : 'border-gray-200 dark:border-slate-700 text-gray-400'
                      }`}
                    >
                      <Icon className="w-4 h-4" />{p.label}
                      {on && <Check className="w-3.5 h-3.5" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slot {anyProdTarget ? '' : <span className="text-gray-400 font-normal">(prod only)</span>}
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setMode('staging')} disabled={!anyProdTarget}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all disabled:opacity-50 ${
                    mode === 'staging' ? 'border-green-500 bg-green-50 dark:bg-green-950/40 text-green-700' : 'border-gray-200 dark:border-slate-700 text-gray-500'
                  }`}>
                  <ShieldCheck className="w-4 h-4" /> Staging
                </button>
                <button type="button" onClick={() => setMode('direct')} disabled={!anyProdTarget}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all disabled:opacity-50 ${
                    mode === 'direct' ? 'border-red-500 bg-red-50 dark:bg-red-950/40 text-red-700' : 'border-gray-200 dark:border-slate-700 text-gray-500'
                  }`}>
                  🚨 Direct
                </button>
              </div>
            </div>
          </div>

          {error && <div className="p-3 bg-red-50 dark:bg-red-950 text-red-700 rounded-lg text-sm">{error}</div>}

          {/* Tenants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Tenants {selected.length > 0 && <span className="text-gray-400">· {selected.length} selected</span>}
              </label>
              {selected.length > 0 && (
                <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setSelected([])}>Clear all</button>
              )}
            </div>
            {tenantsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading tenants…
              </div>
            ) : (
              <div className="space-y-4">
                {tenantSection('Production', rows.prod, 'red')}
                {tenantSection('Non-Production', rows.nonProd, 'gray')}
              </div>
            )}
          </div>

          {/* Deploy plan */}
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 p-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Deploy plan</h4>
            {boTargets.length === 0 && simTargets.length === 0 ? (
              <p className="text-sm text-gray-400">Select at least one tenant and product to deploy.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {boTargets.length > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 mt-0.5">BO</span>
                    <span className="text-gray-700 dark:text-gray-300">
                      <strong>BackOffice</strong> → {boTargets.map(cap).join(', ')}{' '}
                      <span className="text-gray-400">({boTargets.length} × {PRODUCTS.backoffice.pipelineCount} pipelines)</span>
                    </span>
                  </li>
                )}
                {simTargets.length > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700 mt-0.5">SIM</span>
                    <span className="text-gray-700 dark:text-gray-300">
                      <strong>Simulcast</strong> → {simTargets.map(cap).join(', ')}{' '}
                      <span className="text-gray-400">({simTargets.length} × {PRODUCTS.simulcast.pipelineCount} pipelines)</span>
                    </span>
                  </li>
                )}
                {skipped.length > 0 && (
                  <li className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Skipped (selected product not available): {skipped.map(cap).join(', ')}</span>
                  </li>
                )}
              </ul>
            )}
          </div>

          {/* Deploy button */}
          <button
            type="button"
            onClick={handleDeployClick}
            disabled={!canDeploy}
            className={`w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50 ${
              anyProdTarget && mode === 'direct' ? 'bg-red-600 hover:bg-red-700' : anyProdTarget ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            <Rocket className="w-5 h-5" />
            {boTargets.length + simTargets.length === 0
              ? 'Deploy'
              : `Deploy ${[boTargets.length && 'BackOffice', simTargets.length && 'Simulcast'].filter(Boolean).join(' + ')} · ${totalPipelines} pipelines`}
          </button>
        </>
      )}

      {/* Progress — one panel per fired session */}
      {phase === 'progress' && (
        <div className="space-y-4">
          {jobs.map((j) => {
            const s = sessions[j.id];
            const [cls, label] = overallStatusMeta(s?.overallStatus);
            const accent = j.product === 'simulcast' ? 'violet' : 'blue';
            return (
              <div key={j.id} className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="flex items-center gap-2 flex-wrap px-4 py-2.5 bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-800">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${accent === 'violet' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>{j.label}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>
                  {s?.deploymentMode === 'direct'
                    ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">🚨 Direct</span>
                    : <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">✅ Staging</span>}
                  <span className="text-xs text-gray-500">{j.tenants.map(cap).join(', ')}</span>
                  <span className="ml-auto text-xs text-gray-400 font-mono">{branch}</span>
                </div>
                <div className="p-4">
                  {s ? <PipelineTable pipelines={s.pipelines} approvals={approvals} /> : (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Starting…</div>
                  )}
                </div>
              </div>
            );
          })}

          {jobs.some((j) => sessions[j.id]?.overallStatus === 'releasing') && mode !== 'direct' && (
            <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
              <strong>Staging flow:</strong> Pipelines deploy to the staging slot first. An ADO approval (below) is required before the production slot swap.
            </div>
          )}

          {allTerminal && (
            <button type="button" onClick={resetToSelect}
              className="px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 font-medium">
              New Deployment
            </button>
          )}
        </div>
      )}

      {/* Pending approvals */}
      <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Pending approvals</h3>
          <button type="button" className="text-xs text-blue-600 hover:underline" onClick={refreshApprovals}>Refresh</button>
        </div>
        {approvalMsg && (
          <p className={`text-sm mb-2 ${approvalMsg.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>{approvalMsg.text}</p>
        )}
        {approvals.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-slate-400">No pending approvals.</p>
        ) : (
          <ul className="space-y-3">
            {approvals.map((a) => (
              <li key={a.id} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {a.pipelineName && <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-semibold">{a.pipelineName}</span>}
                  {a.runName && (a.runUrl
                    ? <a href={a.runUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-0.5 bg-white border border-amber-200 text-amber-700 rounded text-xs font-mono hover:bg-amber-50">#{a.runName} ↗</a>
                    : <span className="px-2 py-0.5 bg-white border border-amber-200 text-amber-700 rounded text-xs font-mono">#{a.runName}</span>)}
                  <span className="text-xs text-gray-500">{fmtDate(a.createdOn)}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <input type="text" className="flex-1 px-2 py-1.5 border rounded text-sm" placeholder="Comment (optional)"
                    value={approvalComments[a.id] ?? ''} onChange={(e) => setApprovalComments((p) => ({ ...p, [a.id]: e.target.value }))} />
                  <button type="button" disabled={approvalBusy !== null} onClick={() => doApprove(a.id, approvalComments[a.id])}
                    className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                    {approvalBusy === a.id ? '…' : 'Approve'}
                  </button>
                  <button type="button" disabled={approvalBusy !== null} onClick={() => doReject(a.id, approvalComments[a.id])}
                    className="px-3 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                    {approvalBusy === a.id ? '…' : 'Reject'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* History */}
      <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Deployment History</h3>
          <button type="button" className="text-xs text-blue-600 hover:underline disabled:opacity-50" onClick={refreshHistory} disabled={historyLoading}>
            {historyLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {!historyLoading && history.length === 0 && <p className="text-sm text-gray-500 dark:text-slate-400">No deployments yet.</p>}
        <div className="space-y-2">
          {history.map((h) => {
            const [cls, label] = overallStatusMeta(h.overallStatus);
            return (
              <div key={h.sessionId} className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <button type="button" className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-800 text-left"
                  onClick={() => setExpandedHistory((p) => (p === h.sessionId ? null : h.sessionId))}>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${cls}`}>{label}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                    h.product === 'simulcast' ? 'bg-violet-100 text-violet-700' : h.product === 'backoffice' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                  }`}>{h.product === 'simulcast' ? 'Simulcast' : h.product === 'backoffice' ? 'BackOffice' : cap(h.product || '')}</span>
                  {h.deploymentMode === 'direct'
                    ? <span className="px-2 py-0.5 rounded text-xs font-medium shrink-0 bg-red-100 text-red-700">🚨 Direct</span>
                    : <span className="px-2 py-0.5 rounded text-xs font-medium shrink-0 bg-green-100 text-green-700">✅ Staging</span>}
                  <span className="font-medium text-sm text-gray-800 dark:text-gray-200 shrink-0">
                    {Array.isArray(h.tenants) ? (h.tenants.length <= 3 ? h.tenants.map(cap).join(', ') : `${h.tenants.length} tenants`) : ''}
                  </span>
                  <span className="text-sm text-gray-500 font-mono truncate">{h.branch}</span>
                  <span className="ml-auto text-xs text-gray-400 shrink-0">{fmtDate(h.startedAt)}</span>
                  <span className={`text-gray-400 text-xs shrink-0 transition-transform ${expandedHistory === h.sessionId ? 'rotate-180' : ''}`}>▼</span>
                </button>
                {expandedHistory === h.sessionId && (
                  <div className="px-4 pb-3 border-t border-gray-100 dark:border-slate-800 pt-2">
                    <PipelineTable pipelines={h.pipelines} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirmation modal (prod) */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowConfirm(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2">{mode === 'direct' ? '🚨 Confirm Direct Production Deploy' : '✅ Confirm Staging Deploy'}</h3>
            <div className={`mb-4 p-3 rounded-lg text-sm ${mode === 'direct' ? 'bg-red-50 dark:bg-red-950 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800' : 'bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'}`}>
              {mode === 'direct'
                ? <>This deploys <strong>directly to the production slot</strong>, bypassing staging. Live traffic is affected immediately.</>
                : <>This deploys to the <strong>staging slot</strong>. An ADO approval gate appears before the production swap.</>}
              <ul className="mt-2 space-y-0.5">
                {boTargets.length > 0 && <li><strong>BackOffice</strong>: {boTargets.map(cap).join(', ')}</li>}
                {simTargets.length > 0 && <li><strong>Simulcast</strong>: {simTargets.map(cap).join(', ')}</li>}
              </ul>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Type <strong>{confirmationPhrase}</strong> to confirm:</p>
            <input type="text" className="w-full px-3 py-2 border rounded-lg mb-4 dark:bg-slate-800" value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)} placeholder={confirmationPhrase} />
            <div className="flex gap-2 justify-end">
              <button type="button" className="px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button type="button" disabled={confirmInput !== confirmationPhrase} onClick={executeDeploy}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${mode === 'direct' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                Confirm Deploy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDeploy;
